import React, { useEffect, useRef, useState } from "react";
import {
  Asset3DIcon,
  TemplateIcon,
  SceneIcon,
  ImageIcon,
  VideoIcon,
  AudioIcon,
  TextIcon,
  VectorIcon,
  SpriteIcon,
} from "./CategoryIcons";
import Pagination from "./Pagination";
import AddAssetModal from "./AddAssetModal";
import {
  FaEye,
  FaTrash,
  FaPlus,
  FaTimes,
  FaSearch,
  FaInbox,
  FaEdit,
  FaCloudUploadAlt,
  FaTag,
  FaCheckCircle,
  FaCode,
  FaCopy,
} from "react-icons/fa";

export default function MainContent() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeCategory, setActiveCategory] = useState(0);
  const [apiLibrary, setApiLibrary] = useState([]);
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [authError, setAuthError] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const searchDebounceRef = useRef(null);

  // Embed modal state
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [embedItem, setEmbedItem] = useState(null);
  const [embedSize, setEmbedSize] = useState("640x480");
  const [embedCopied, setEmbedCopied] = useState(false);

  const getEmbedCode = (item, width, height) => {
    const id = item._id || item.id || "";
    const name = item.name || item.title || "Asset";
    const origin = window.location.origin;
    // Only the asset ID goes in the URL — the actual storage URL is never
    // exposed here. The /embed route fetches it server-side and streams bytes.
    const src = `${origin}/embed?id=${id}`;
    const w = width === "100%" ? "100%" : `${width}px`;
    return `<div class="spatialgrid-embed-wrapper">
  <iframe
    title="${name}"
    frameborder="0"
    allowfullscreen
    mozallowfullscreen="true"
    webkitallowfullscreen="true"
    allow="autoplay; fullscreen; xr-spatial-tracking"
    xr-spatial-tracking
    execution-while-out-of-viewport
    execution-while-not-rendered
    web-share
    src="${src}"
    style="width:${w};height:${height}px;border:none;">
  </iframe>
  <p style="font-size:13px;font-weight:normal;margin:5px;color:#4A4A4A;">
    <a href="${origin}" target="_blank" rel="nofollow" style="font-weight:bold;color:#EE2B2A;">${name}</a>
    on
    <a href="https://spatialgrid.com" target="_blank" rel="nofollow" style="font-weight:bold;color:#EE2B2A;">Spatialgrid</a>
  </p>
</div>`;
  };

  const openEmbed = (item) => {
    setEmbedItem(item);
    setEmbedSize("640x480");
    setEmbedCopied(false);
    setShowEmbedModal(true);
  };

  const handleCopyEmbed = () => {
    if (!embedItem) return;
    const [w, h] = embedSize === "responsive" ? ["100%", "480"] : embedSize.split("x");
    const code = getEmbedCode(embedItem, w, h);
    navigator.clipboard.writeText(code).then(() => {
      setEmbedCopied(true);
      setTimeout(() => setEmbedCopied(false), 2500);
    });
  };

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", type: "", subtype: "", industry: "", isPublic: false });
  const [editThumbnail, setEditThumbnail] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [editTags, setEditTags] = useState([]);
  const [editNewTag, setEditNewTag] = useState("");
  const [isPatching, setIsPatching] = useState(false);
  const [patchMessage, setPatchMessage] = useState("");
  const [patchStatus, setPatchStatus] = useState("");

  /* ── Categories ─────────────────────────────────────────────── */
  const categories = [
    { name: "3D Assets", icon: Asset3DIcon, type: "3D" },
    { name: "Templates", icon: TemplateIcon, type: "template" },
    { name: "Scenes", icon: SceneIcon, type: "scene" },
    { name: "Image", icon: ImageIcon, type: "image" },
    { name: "Video", icon: VideoIcon, type: "video" },
    { name: "Audio", icon: AudioIcon, type: "audio" },
    { name: "Text", icon: TextIcon, type: "text" },
    { name: "Vectors", icon: VectorIcon, type: "vector" },
    { name: "Sprites", icon: SpriteIcon, type: "sprite" },
  ];

  const activeCategoryObj =
    activeCategory !== null ? categories[activeCategory] : null;

  /* ── Fetch data ──────────────────────────────────────────────── */
  const fetchData = async ({ query = "", type = "" } = {}) => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setAuthError("No access token found. Please log in again.");
      return;
    }
    const params = new URLSearchParams();
    if (query.trim()) params.set("query", query.trim());
    if (type.trim()) params.set("type", type.trim());
    const qs = params.toString() ? `?${params.toString()}` : "";
    setIsFetching(true);
    try {
      const res = await fetch(
        `https://devserver.spatialgrid.ai/api/v1/library${qs}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!res.ok) {
        if (res.status === 401) {
          setAuthError("Unauthorized. Please log in again.");
          ["accessToken", "refreshToken", "userData"].forEach((k) =>
            localStorage.removeItem(k),
          );
          return;
        }
        throw new Error("Network response was not ok");
      }
      const data = await res.json();
      // When filtered by type the API groups results by subtype: [{ subtype, data: [...] }]
      // When unfiltered it returns a flat array of items
      const items = Array.isArray(data.data) && data.data[0]?.data
        ? data.data.flatMap((group) => group.data)
        : (data.data ?? []);
      setApiLibrary(items);
      setTotalCount(data.total ?? items.length);
      setAuthError("");
    } catch (err) {
      setAuthError("Failed to fetch library data. Please try again.");
    } finally {
      setIsFetching(false);
    }
  };

  const prevTypeFilterRef = useRef(typeFilter);

  // Re-fetch on search query (debounced 400ms) or type change (immediate)
  useEffect(() => {
    const isTypeChange = prevTypeFilterRef.current !== typeFilter;
    prevTypeFilterRef.current = typeFilter;
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchData({ query: searchQuery, type: typeFilter });
      setCurrentPage(1);
    }, isTypeChange ? 0 : 400);
    return () => clearTimeout(searchDebounceRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, typeFilter]);

  /* ── Preview ─────────────────────────────────────────────────── */
  const [previewItem, setPreviewItem] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const openPreview = (item) => {
    const raw = item.url || item.fileUrl || item.assetUrl || "";
    const url = raw.startsWith("http") ? raw : `https://devserver.spatialgrid.ai${raw}`;
    setPreviewLoading(true);
    setPreviewItem({ ...item, resolvedUrl: url });
    setShowViewerModal(true);
  };

  useEffect(() => {
    if (!previewItem) return;
    const hasLoadEvent = previewItem.resolvedUrl && ["3D", "image", "video", "audio"].includes(previewItem.type);
    if (!hasLoadEvent) setPreviewLoading(false);
  }, [previewItem]);

  /* ── Delete ──────────────────────────────────────────────────── */
  const confirmDelete = (item) => {
    setItemToDelete(item);
    setDeleteMessage("");
    setShowDeleteModal(true);
  };

  const handleDeleteItem = async () => {
    const id = itemToDelete?._id || itemToDelete?.id;
    if (!id) {
      setDeleteMessage("Error: Could not find item ID.");
      return;
    }
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setDeleteMessage("Error: No access token found. Please log in again.");
      return;
    }
    setIsDeleting(true);
    setDeleteMessage("");
    try {
      const res = await fetch(
        `https://devserver.spatialgrid.ai/api/v1/library/${id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!res.ok) {
        if (res.status === 401) {
          ["accessToken", "refreshToken", "userData"].forEach((k) =>
            localStorage.removeItem(k),
          );
          setDeleteMessage("Error: Unauthorized. Please log in again.");
          return;
        }
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Server error (${res.status})`);
      }
      setApiLibrary((prev) => prev.filter((i) => (i._id || i.id) !== id));
      setDeleteMessage("Item deleted successfully!");
      setTimeout(() => {
        setShowDeleteModal(false);
        setItemToDelete(null);
        setDeleteMessage("");
      }, 1500);
    } catch (err) {
      setDeleteMessage(`Error: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  /* ── Edit ────────────────────────────────────────────────────── */
  const openEditModal = (item) => {
    setEditItem(item);
    setEditForm({
      name: item.name || item.title || "",
      type: item.type || "",
      subtype: item.subtype || "",
      industry: item.industry || "",
      isPublic: !!item.isPublic,
    });
    const rawTags = item.tags;
    setEditTags(
      Array.isArray(rawTags) ? rawTags :
      typeof rawTags === "string" ? (() => { try { return JSON.parse(rawTags); } catch { return []; } })() :
      []
    );
    setEditThumbnail(null);
    setEditFile(null);
    setPatchMessage("");
    setPatchStatus("");
    setShowEditModal(true);
  };

  const handlePatch = async () => {
    const id = editItem?._id || editItem?.id;
    if (!id) {
      setPatchStatus("error");
      setPatchMessage("Error: Could not find asset ID.");
      return;
    }
    const token = localStorage.getItem("accessToken");
    if (!token) { setPatchStatus("error"); setPatchMessage("No access token found."); return; }
    setIsPatching(true);
    setPatchMessage("");
    setPatchStatus("");
    try {
      const fd = new FormData();
      fd.append("name", editForm.name);
      fd.append("type", editForm.type);
      fd.append("subtype", editForm.subtype);
      fd.append("industry", editForm.industry);
      fd.append("isPublic", editForm.isPublic === true ? "true" : "false");
      fd.append("tags", JSON.stringify(editTags));
      if (editThumbnail) fd.append("thumbnail", editThumbnail);
      if (editFile) fd.append("file", editFile);

      // Preserve existing metadata; if a new file is uploaded update fileSize + format
      const existingMeta = editItem?.metadata ?? null;
      const patchMeta = editFile
        ? { ...(existingMeta || {}), fileSize: editFile.size, format: editFile.name.split(".").pop().toLowerCase() }
        : existingMeta;
      if (patchMeta) {
        fd.append("metadata", JSON.stringify(patchMeta));
        if (patchMeta.fileSize != null)  fd.append("fileSize", String(patchMeta.fileSize));
        if (patchMeta.format)            fd.append("format", patchMeta.format);
        if (patchMeta.resolution)        fd.append("resolution", patchMeta.resolution);
        if (patchMeta.polyCount != null) fd.append("polyCount", String(patchMeta.polyCount));
      }

      const res = await fetch(`https://devserver.spatialgrid.ai/api/v1/library/${id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(result.message || `HTTP ${res.status} — check asset ID and endpoint`);

      setApiLibrary((prev) => prev.map((i) => (i._id || i.id) === id ? { ...i, ...result.data } : i));
      setPatchStatus("success");
      setPatchMessage("Asset updated successfully!");
      setTimeout(() => setShowEditModal(false), 1200);
    } catch (err) {
      setPatchStatus("error");
      setPatchMessage(err.message);
    } finally {
      setIsPatching(false);
    }
  };

  /* ── Paginate (API already filtered) ────────────────────────── */
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = apiLibrary.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(apiLibrary.length / rowsPerPage);

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  /* ── Empty state label ───────────────────────────────────────── */
  const normalizedTypeFilter = typeFilter?.trim().toLowerCase() ?? "";
  const typeFilterObj = normalizedTypeFilter
    ? categories.find((c) => c.type.trim().toLowerCase() === normalizedTypeFilter) ?? null
    : null;

  const emptyLabel = typeFilterObj
    ? (searchQuery
      ? `No "${typeFilterObj.name}" assets match "${searchQuery}"`
      : `No "${typeFilterObj.name}" assets available`)
    : typeFilter
      ? (searchQuery
        ? `No "${typeFilter}" assets match "${searchQuery}"`
        : `No "${typeFilter}" assets available`)
      : activeCategoryObj
        ? `No "${activeCategoryObj.name}" assets available`
        : searchQuery
          ? `No assets match "${searchQuery}"`
          : "No assets found";

  /* ═══════════════════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════════════════ */
  return (
    <div className="flex-1 p-6 overflow-x-hidden bg-[#121316] max-h-screen">
      {/* Auth error banner */}
      {authError && (
        <div className="p-4 mb-6 rounded-xl bg-red-500/20 text-red-400 border border-red-500/30">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="font-medium text-sm">{authError}</span>
          </div>
        </div>
      )}

      <div className="bg-[#1E1F22] rounded-2xl p-6 shadow-2xl border border-gray-800">
        {/* ── Header ── */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-100 mb-1">
              Library
            </h1>
            <p className="text-gray-400 text-sm">Manage your digital assets</p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
            {/* Search + Type filter */}
            <div className="flex items-center w-full sm:w-auto bg-[#2d2e32] border border-[#444447] rounded-xl overflow-hidden transition-all duration-200 focus-within:border-[#EE2B2A]">
              <FaSearch className="shrink-0 ml-4 text-gray-500 text-sm" />
              <input
                type="text"
                placeholder="Search assets..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="w-full sm:w-[160px] px-3 py-2 bg-transparent text-gray-300 outline-none text-sm"
              />
              {(searchQuery || typeFilter) && (
                <button
                  onClick={() => {
                    // Clear filters consistently (avoid combining category + dropdown accidentally)
                    setSearchQuery("");
                    setTypeFilter("");
                    setActiveCategory(null);
                    setCurrentPage(1);
                  }}
                  className="shrink-0 mr-1 w-5 h-5 flex items-center justify-center rounded-full bg-[#444447] text-gray-400 hover:bg-[#EE2B2A]/20 hover:text-[#EE2B2A] transition-all duration-150"
                  title="Clear filters"
                >
                  <FaTimes className="text-[9px]" />
                </button>
              )}
              <div className="w-px self-stretch bg-[#444447] shrink-0" />
              <select
                value={typeFilter}
                onChange={(e) => {
                  const nextType = e.target.value?.trim() ?? "";
                  setTypeFilter(nextType);
                  // Keep the category buttons visually in sync with dropdown selection.
                  const matchIndex = categories.findIndex(
                    (c) => c.type.trim().toLowerCase() === nextType.trim().toLowerCase()
                  );
                  setActiveCategory(matchIndex === -1 ? null : matchIndex);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 bg-[#2d2e32] text-gray-300 text-sm outline-none cursor-pointer appearance-none pr-7"
                style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center" }}
              >
                <option value="" className="bg-[#2d2e32] text-gray-300">All Types</option>
                <option value="3D" className="bg-[#2d2e32] text-gray-300">3D Assets</option>
                <option value="template" className="bg-[#2d2e32] text-gray-300">Templates</option>
                <option value="scene" className="bg-[#2d2e32] text-gray-300">Scenes</option>
                <option value="image" className="bg-[#2d2e32] text-gray-300">Image</option>
                <option value="video" className="bg-[#2d2e32] text-gray-300">Video</option>
                <option value="audio" className="bg-[#2d2e32] text-gray-300">Audio</option>
                <option value="text" className="bg-[#2d2e32] text-gray-300">Text</option>
                <option value="vector" className="bg-[#2d2e32] text-gray-300">Vectors</option>
                <option value="sprite" className="bg-[#2d2e32] text-gray-300">Sprites</option>
              </select>
            </div>
            {/* Add Asset */}
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center justify-center gap-2 px-5 py-2 border-2 border-[#EE2B2A] text-white rounded-xl font-medium text-sm transition-all duration-200 hover:bg-[#EE2B2A]/10 whitespace-nowrap"
            >
              <FaPlus className="text-xs" />
              Add Asset
            </button>
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-8 mb-8">
          {categories.map((cat, index) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.name}
                className={`flex items-center gap-2 px-6 py-2.5 bg-[#2d2e32] border border-[#444447] rounded-xl cursor-pointer text-sm font-medium transition-all duration-300 ${
                  activeCategory === index
                    ? "bg-[#444447] text-white"
                    : "text-white hover:bg-[#444447]"
                }`}
                onClick={() => {
                      const nextActive =
                        activeCategory === index ? null : index;
                      setActiveCategory(nextActive);
                      // Keep the dropdown in sync with the category selection.
                      setTypeFilter(nextActive == null ? "" : categories[nextActive].type);
                  setCurrentPage(1);
                }}
              >
                <Icon className="w-5 h-5" />
                <span className="whitespace-nowrap">{cat.name}</span>
              </button>
            );
          })}
        </div>

        {/* ── Table ── */}
        <div className="w-full overflow-x-auto rounded-xl border border-gray-800">
          <table className="w-full border-collapse table-fixed">
            <colgroup>
              <col className="w-[22%]" />
              <col className="w-[24%]" />
              <col className="w-[10%]" />
              <col className="w-[10%]" />
              <col className="w-[12%]" />
              <col className="w-[20%]" />
            </colgroup>
            <thead>
              <tr className="bg-[#444445]">
                {[
                  "Name",
                  "Description",
                  "Type",
                  "Public",
                  "Thumbnail",
                  "Actions",
                ].map((h) => (
                  <th
                    key={h}
                    className={`p-4 text-gray-300 font-semibold text-sm uppercase tracking-wider border-b border-gray-800 ${
                      ["Public", "Thumbnail", "Actions"].includes(h)
                        ? "text-center"
                        : "text-left"
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentRows.length > 0 ? (
                currentRows.map((item, index) => (
                  <tr
                    key={item._id ?? index}
                    className="bg-[#1E1F22] hover:bg-[#2A2A2B] border-b border-gray-800/50 transition-all duration-200"
                  >
                    <td className="p-3 text-gray-200 font-medium">
                      {item.name || item.title}
                    </td>
                    <td className="p-3 text-gray-400 text-sm max-w-xs truncate">
                      {item.description}
                    </td>
                    <td className="p-3">
                      <span className="px-3 py-1 bg-[#444445] text-white rounded-full text-xs font-medium border border-blue-500/30">
                        {item.type}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      {item.isPublic ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/15 text-green-400 border border-green-500/30">
                          Public
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-500/15 text-gray-500 border border-gray-600/30">
                          Private
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex justify-center">
                        <div className="w-12 h-12 rounded-lg overflow-hidden border-2 border-gray-700 transition-colors duration-200">
                          <img
                            src={item.thumbnail}
                            alt={item.name || item.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openPreview(item)}
                          className="inline-flex items-center justify-center w-9 h-9 bg-[#444445]/60 hover:bg-[#444445] text-gray-300 rounded-lg transition-all duration-200 border border-gray-600/40 hover:border-gray-500"
                          title="Preview"
                        >
                          <FaEye className="text-sm" />
                        </button>
                        <button
                          onClick={() => openEditModal(item)}
                          className="inline-flex items-center justify-center w-9 h-9 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-all duration-200 border border-blue-500/30 hover:border-blue-500"
                          title="Edit"
                        >
                          <FaEdit className="text-sm" />
                        </button>
                        <button
                          onClick={() => openEmbed(item)}
                          className="inline-flex items-center justify-center w-9 h-9 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg transition-all duration-200 border border-purple-500/30 hover:border-purple-500"
                          title="Get Embed Code"
                        >
                          <FaCode className="text-sm" />
                        </button>
                        <button
                          onClick={() => confirmDelete(item)}
                          className="inline-flex items-center justify-center w-9 h-9 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-all duration-200 border border-red-500/30 hover:border-red-500"
                          title="Delete"
                        >
                          <FaTrash className="text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="p-12 text-center text-gray-500">
                    <div className="flex flex-col items-center gap-3">
                      <FaSearch className="text-4xl opacity-50" />
                      <p className="text-lg">{emptyLabel}</p>
                      <p className="text-sm">
                        Try adjusting your search or filters
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination + Rows per page ── */}
        <div className="mt-5 flex items-center justify-between gap-4">
          {/* Left: rows per page + count */}
          <div className="flex items-center gap-2.5">
            <span className="text-xs text-gray-500 whitespace-nowrap">
              Rows per page
            </span>
            <select
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              className="px-2.5 py-1.5 rounded-lg bg-[#2d2e32] text-gray-300 border border-[#444447] text-xs outline-none focus:border-[#EE2B2A] transition-colors duration-200 cursor-pointer"
            >
              {[ 10, 25, 50,100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <span className="text-xs text-gray-600 whitespace-nowrap">
              {apiLibrary.length === 0
                ? "0 results"
                : `${indexOfFirstRow + 1}–${Math.min(indexOfLastRow, apiLibrary.length)} of ${apiLibrary.length}`}
            </span>
          </div>

          {/* Right: pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      {/* ── Add Asset Modal ── */}
      <AddAssetModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={fetchData}
      />

      {/* ── Preview Modal ── */}
      {showViewerModal && previewItem && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => { setShowViewerModal(false); setPreviewLoading(false); }}
        >
          <div
            className="bg-[#1E1F22] border border-gray-700 p-6 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-auto relative shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-red-500/20 hover:bg-red-500 text-red-400 hover:text-white border border-red-500/30 transition-all duration-200"
              onClick={() => { setShowViewerModal(false); setPreviewLoading(false); }}
            >
              <FaTimes />
            </button>
            <h2 className="text-xl font-bold text-gray-100 mb-5">
              {previewItem.name || previewItem.title || "Preview"}
            </h2>
            <div className="relative w-full h-[480px] bg-[#121316] rounded-xl overflow-hidden border border-gray-700 flex items-center justify-center">
              {/* Loading overlay */}
              {previewLoading && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[#121316] gap-4">
                  <div className="w-12 h-12 rounded-full border-[3px] border-gray-700 border-t-[#EE2B2A] animate-spin" />
                  <p className="text-gray-500 text-sm tracking-wide">Loading preview…</p>
                </div>
              )}

              {!previewItem.resolvedUrl ? (
                <p className="text-gray-500 text-sm">No file URL available for this asset.</p>
              ) : previewItem.type === "3D" ? (
                <model-viewer
                  alt="3D Model"
                  src={previewItem.resolvedUrl}
                  ar
                  shadow-intensity="1"
                  camera-controls
                  touch-action="pan-y"
                  style={{ width: "100%", height: "100%" }}
                  ref={(el) => {
                    if (el) el.addEventListener("load", () => setPreviewLoading(false), { once: true });
                  }}
                />
              ) : previewItem.type === "image" ? (
                <img
                  src={previewItem.resolvedUrl}
                  alt={previewItem.name || previewItem.title}
                  className="max-w-full max-h-full object-contain"
                  onLoad={() => setPreviewLoading(false)}
                />
              ) : previewItem.type === "video" ? (
                <video
                  src={previewItem.resolvedUrl}
                  controls
                  className="max-w-full max-h-full"
                  onCanPlay={() => setPreviewLoading(false)}
                />
              ) : previewItem.type === "audio" ? (
                <div className="flex flex-col items-center gap-4">
                  <FaInbox className="text-6xl text-gray-600" />
                  <audio
                    src={previewItem.resolvedUrl}
                    controls
                    onCanPlay={() => setPreviewLoading(false)}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3 text-gray-500">
                  <FaInbox className="text-5xl opacity-40" />
                  <p className="text-sm">No preview available for this file type.</p>
                  <a
                    href={previewItem.resolvedUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#EE2B2A] text-sm underline"
                  >
                    Open file
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ── */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-[#1E1F22] border border-gray-700 p-6 rounded-2xl w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="mx-auto w-14 h-14 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <FaTrash className="text-red-500 text-xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-100 mb-2">
                Delete Item?
              </h3>
              <p className="text-gray-400 text-sm mb-6">
                This action cannot be undone. The item will be permanently
                removed from your library.
              </p>

              {deleteMessage && (
                <div
                  className={`p-3 mb-4 rounded-lg text-sm border ${
                    deleteMessage.includes("Error")
                      ? "bg-red-500/10 text-red-400 border-red-500/30"
                      : "bg-green-500/10 text-green-400 border-green-500/30"
                  }`}
                >
                  {deleteMessage}
                </div>
              )}

              <div className="flex gap-3 justify-center">
                <button
                  onClick={handleDeleteItem}
                  disabled={isDeleting}
                  className="px-6 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setItemToDelete(null);
                    setDeleteMessage("");
                  }}
                  disabled={isDeleting}
                  className="px-6 py-2.5 bg-[#2d2e32] hover:bg-[#3a3b40] text-gray-300 rounded-xl text-sm font-medium border border-gray-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {showEditModal && editItem && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowEditModal(false)}
        >
          <div
            className="bg-[#1E1F22] border border-gray-700 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ scrollbarWidth: "none" }}
          >
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 bg-[#1e1f22] border-b border-gray-800 rounded-t-2xl">
              <div>
                <h2 className="text-lg font-bold text-gray-100">Edit Asset</h2>
                <p className="text-xs text-gray-500 mt-0.5">Update asset details</p>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-[#26272b] border border-gray-700 text-gray-500 hover:bg-[#EE2B2A] hover:text-white hover:border-[#EE2B2A] transition-all duration-200"
              >
                <FaTimes />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 flex flex-col gap-5">

              {/* Name */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1.5">Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl bg-[#2d2e32] text-gray-200 border border-[#2e2f33] text-sm outline-none focus:border-[#EE2B2A] focus:ring-2 focus:ring-[#EE2B2A]/10 transition-all duration-200"
                />
              </div>

              {/* Type & Subtype */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1.5">Type</label>
                  <select
                    value={editForm.type}
                    onChange={(e) => setEditForm((p) => ({ ...p, type: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl bg-[#2d2e32] text-gray-200 border border-[#2e2f33] text-sm outline-none focus:border-[#EE2B2A] transition-all duration-200"
                  >
                    <option value="">Select type…</option>
                    <option value="3D">3D Assets</option>
                    <option value="template">Templates</option>
                    <option value="scene">Scenes</option>
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                    <option value="audio">Audio</option>
                    <option value="text">Text</option>
                    <option value="vector">Vectors</option>
                    <option value="sprite">Sprites</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1.5">Category</label>
                  <select
                    value={editForm.subtype}
                    onChange={(e) => setEditForm((p) => ({ ...p, subtype: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl bg-[#2d2e32] text-gray-200 border border-[#2e2f33] text-sm outline-none focus:border-[#EE2B2A] transition-all duration-200"
                  >
                    <option value="">Select category…</option>
                    <option value="character">Character</option>
                    <option value="object">Object</option>
                  </select>
                </div>
              </div>

              {/* Industry */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1.5">Industry</label>
                <select
                  value={editForm.industry}
                  onChange={(e) => setEditForm((p) => ({ ...p, industry: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl bg-[#2d2e32] text-gray-200 border border-[#2e2f33] text-sm outline-none focus:border-[#EE2B2A] transition-all duration-200"
                >
                  <option value="">Select industry…</option>
                  <option value="Manufacturing & Industrial">Manufacturing &amp; Industrial</option>
                  <option value="Healthcare and Pharma">Healthcare and Pharma</option>
                  <option value="Retail & E-Commerce">Retail &amp; E-Commerce</option>
                  <option value="Education & Training">Education &amp; Training</option>
                  <option value="Automotive & Transportation">Automotive &amp; Transportation</option>
                  <option value="Architecture & Real Estate">Architecture &amp; Real Estate</option>
                  <option value="Defense & Aerospace">Defense &amp; Aerospace</option>
                  <option value="Energy and Mining">Energy and Mining</option>
                  <option value="Tourism & Hospitality">Tourism &amp; Hospitality</option>
                </select>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1.5">Tags</label>
                <div className="flex flex-wrap gap-2 min-h-[44px] px-3 py-2.5 bg-[#2d2e32] border border-[#2e2f33] rounded-xl mb-2">
                  {editTags.length === 0 && <span className="text-xs text-gray-600 self-center">No tags</span>}
                  {editTags.map((tag) => (
                    <span key={tag} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#EE2B2A]/15 text-[#EE2B2A] border border-[#EE2B2A]/30">
                      {tag}
                      <button type="button" onClick={() => setEditTags((p) => p.filter((t) => t !== tag))} className="hover:text-white transition-colors">
                        <FaTimes className="text-[9px]" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editNewTag}
                    onChange={(e) => setEditNewTag(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const t = editNewTag.trim().toLowerCase();
                        if (t && !editTags.includes(t)) setEditTags((p) => [...p, t]);
                        setEditNewTag("");
                      }
                    }}
                    placeholder="Add tag and press Enter…"
                    className="flex-1 px-4 py-2 rounded-xl bg-[#2d2e32] text-gray-200 border border-[#2e2f33] text-sm outline-none focus:border-[#EE2B2A] transition-all duration-200"
                  />
                </div>
              </div>

              {/* Thumbnail */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1.5">New Thumbnail <span className="normal-case text-gray-600">(optional)</span></label>
                <input type="file" id="edit-thumb" accept="image/*" className="hidden" onChange={(e) => setEditThumbnail(e.target.files[0])} />
                <label
                  htmlFor="edit-thumb"
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200 ${editThumbnail ? "border-[#EE2B2A] bg-[#EE2B2A]/5" : "border-[#2e2f33] bg-[#2d2e32]"}`}
                >
                  <FaCloudUploadAlt className={`text-xl shrink-0 ${editThumbnail ? "text-[#EE2B2A]" : "text-gray-600"}`} />
                  <span className={`text-sm truncate ${editThumbnail ? "text-gray-200" : "text-gray-600"}`}>
                    {editThumbnail ? editThumbnail.name : "Click to upload new thumbnail"}
                  </span>
                </label>
              </div>

              {/* Asset File (GLB / any) */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1.5">
                  Asset File <span className="normal-case text-gray-600">(optional — replaces existing)</span>
                </label>
                <input type="file" id="edit-file" className="hidden" onChange={(e) => setEditFile(e.target.files[0])} />
                <label
                  htmlFor="edit-file"
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200 ${editFile ? "border-[#EE2B2A] bg-[#EE2B2A]/5" : "border-[#2e2f33] bg-[#2d2e32]"}`}
                >
                  <FaCloudUploadAlt className={`text-xl shrink-0 ${editFile ? "text-[#EE2B2A]" : "text-gray-600"}`} />
                  <span className={`text-sm truncate ${editFile ? "text-gray-200" : "text-gray-600"}`}>
                    {editFile ? editFile.name : "Click to upload new asset file (.glb, etc.)"}
                  </span>
                </label>
              </div>

              {/* isPublic */}
              <label className="flex items-center gap-3 px-4 py-3 bg-[#2d2e32] border border-[#2e2f33] rounded-xl cursor-pointer">
                <input
                  type="checkbox"
                  checked={editForm.isPublic}
                  onChange={(e) => setEditForm((p) => ({ ...p, isPublic: e.target.checked }))}
                  className="w-4 h-4 accent-[#EE2B2A] cursor-pointer"
                />
                <span className="text-sm text-gray-400 select-none">Make this asset publicly accessible</span>
              </label>

              {/* Status */}
              {patchMessage && (
                <div className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm border ${patchStatus === "success" ? "bg-green-500/10 border-green-500/25 text-green-400" : "bg-red-500/10 border-red-500/25 text-red-400"}`}>
                  {patchStatus === "success" ? <FaCheckCircle className="shrink-0" /> : <FaTimes className="shrink-0" />}
                  {patchMessage}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-800">
                <button
                  onClick={() => setShowEditModal(false)}
                  disabled={isPatching}
                  className="px-5 py-2.5 rounded-xl text-sm font-medium bg-[#26272b] hover:bg-[#303136] text-gray-400 border border-gray-700 transition-all duration-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePatch}
                  disabled={isPatching}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#EE2B2A] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPatching ? (
                    <><span className="w-4 h-4 border-2 border-white/25 border-t-white rounded-full animate-spin" />Saving…</>
                  ) : (
                    <><FaCheckCircle />Save Changes</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Embed Modal ── */}
      {showEmbedModal && embedItem && (() => {
        const [w, h] = embedSize === "responsive" ? ["100%", "480"] : embedSize.split("x");
        const code = getEmbedCode(embedItem, w, h);
        return (
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowEmbedModal(false)}
          >
            <div
              className="bg-[#1E1F22] border border-gray-700 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-800">
                <div>
                  <h2 className="text-lg font-bold text-gray-100 flex items-center gap-2">
                    <FaCode className="text-purple-400" />
                    Embed Asset
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Copy the iframe code to embed this asset anywhere
                  </p>
                </div>
                <button
                  onClick={() => setShowEmbedModal(false)}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-[#26272b] border border-gray-700 text-gray-500 hover:bg-[#EE2B2A] hover:text-white hover:border-[#EE2B2A] transition-all duration-200"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="px-6 py-5 flex flex-col gap-4">
                {/* Asset name pill */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Asset</span>
                  <span className="px-3 py-1 bg-[#2d2e32] border border-gray-700 rounded-full text-sm text-gray-200 font-medium">
                    {embedItem.name || embedItem.title}
                  </span>
                  <span className="px-2.5 py-1 bg-purple-500/15 border border-purple-500/30 text-purple-300 rounded-full text-xs">
                    {embedItem.type}
                  </span>
                </div>

                {/* Size selector */}
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-2">
                    Embed Size
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { value: "640x480", label: "640 × 480" },
                      { value: "800x600", label: "800 × 600" },
                      { value: "1280x720", label: "1280 × 720" },
                      { value: "responsive", label: "Responsive" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setEmbedSize(opt.value)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150 ${
                          embedSize === opt.value
                            ? "bg-purple-500/20 border-purple-500/60 text-purple-300"
                            : "bg-[#2d2e32] border-gray-700 text-gray-400 hover:border-gray-500"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Code block */}
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-2">
                    Embed Code
                  </label>
                  <div className="relative">
                    <pre
                      className="w-full p-4 bg-[#121316] border border-gray-800 rounded-xl text-xs text-gray-300 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed"
                      style={{ maxHeight: 220, overflowY: "auto", fontFamily: "'Fira Code', 'Cascadia Code', monospace" }}
                    >
                      {code}
                    </pre>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-1">
                  <p className="text-xs text-gray-600">
                    Paste this snippet wherever you want to display the asset.
                  </p>
                  <button
                    onClick={handleCopyEmbed}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                      embedCopied
                        ? "bg-green-500/20 border border-green-500/40 text-green-400"
                        : "bg-purple-500/20 border border-purple-500/40 text-purple-300 hover:bg-purple-500/30"
                    }`}
                  >
                    {embedCopied ? (
                      <><FaCheckCircle />Copied!</>
                    ) : (
                      <><FaCopy />Copy Code</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
